import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Stack,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AdminFormRow from '../../../../components/admin/AdminFormRow';
import { adminColors } from '../../../../styles/adminDesignTokens';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
  adminDialogActionsSx,
  adminDialogContentSx,
  adminDialogSx,
  adminDialogTitleSx,
  adminFormControlSx,
  adminFormFieldSx,
} from '../../../../styles/adminComponentStyles';
import type { StoreLocationType } from '../../../../types/features/storeDetails/locations';

type AddLocationDialogProps = {
  mode: 'create' | 'edit';
  open: boolean;
  error: string;
  locationKey: string;
  locationName: string;
  locationType: StoreLocationType | '';
  vendingBinCount?: string;
  submitting: boolean;
  onClose: () => void;
  onLocationKeyChange: (value: string) => void;
  onLocationNameChange: (value: string) => void;
  onLocationTypeChange: (value: StoreLocationType | '') => void;
  onVendingBinCountChange?: (value: string) => void;
  onSubmit: () => void;
};

const AddLocationDialog: React.FC<AddLocationDialogProps> = ({
  mode,
  open,
  error,
  locationKey,
  locationName,
  locationType,
  vendingBinCount = '',
  submitting,
  onClose,
  onLocationKeyChange,
  onLocationNameChange,
  onLocationTypeChange,
  onVendingBinCountChange,
  onSubmit,
}) => {
  const isEditMode = mode === 'edit';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: adminCombineSx(adminDialogSx, { maxWidth: '570px' }),
      }}
    >
      <DialogTitle
        sx={adminCombineSx(adminDialogTitleSx, {
          backgroundColor: 'rgb(239, 246, 255)',
          color: adminColors.primary,
          fontWeight: 600,
          marginBottom: 0,
        })}
      >
        {isEditMode ? 'Update Location' : 'Add Location'}
      </DialogTitle>
      <DialogContent sx={adminCombineSx(adminDialogContentSx, { p: '20px', pt: '20px !important' })}>
        <Stack spacing={2}>
          {error ? (
            <Typography sx={{ color: adminColors.danger }}>
              {error}
            </Typography>
          ) : null}
          <AdminFormRow label="Key" required={!isEditMode}>
            <TextField
              fullWidth
              value={locationKey}
              onChange={(event) => onLocationKeyChange(event.target.value)}
              disabled={submitting || isEditMode}
              inputProps={{ 'aria-label': 'Location Key' }}
              sx={adminFormFieldSx}
            />
          </AdminFormRow>
          <AdminFormRow label="Name" required>
            <TextField
              fullWidth
              value={locationName}
              onChange={(event) => onLocationNameChange(event.target.value)}
              disabled={submitting}
              inputProps={{ 'aria-label': 'Location Name' }}
              sx={adminFormFieldSx}
            />
          </AdminFormRow>
          <AdminFormRow label="Type" required>
            <FormControl fullWidth disabled={submitting || isEditMode} sx={adminFormControlSx}>
              <Select
                value={locationType}
                displayEmpty
                inputProps={{ 'aria-label': 'Location Type' }}
                renderValue={(selected) => (selected ? String(selected) : 'Select Type')}
                onChange={(event) => onLocationTypeChange(event.target.value as StoreLocationType | '')}
              >
                <MenuItem value="counter">Counter</MenuItem>
                <MenuItem value="vending">Vending</MenuItem>
              </Select>
            </FormControl>
          </AdminFormRow>
          {locationType === 'vending' ? (
            <AdminFormRow label="Bins" required>
              <TextField
                fullWidth
                type="number"
                value={vendingBinCount}
                onChange={(event) => onVendingBinCountChange?.(event.target.value)}
                disabled={submitting}
                inputProps={{ 'aria-label': 'Number of Bins', min: 1, max: 500, step: 1 }}
                sx={adminFormFieldSx}
              />
            </AdminFormRow>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={adminDialogActionsSx}>
        <Button
          disabled={submitting}
          onClick={onClose}
          variant="outlined"
          sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary, { height: 40 })}
        >
          Cancel
        </Button>
        <Button
          disabled={submitting}
          onClick={onSubmit}
          variant="contained"
          sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary, { height: 40 })}
        >
          {submitting ? 'Saving...' : isEditMode ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(AddLocationDialog);
